// ===============================================================
// ⚙️ MessagesSettingsPage.js — Messages Settings
// path: src/components/MessagesSettingsPage.js
// ===============================================================

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { auth, db } from "../firebase";
import "./MessagesSettingsPage.css";

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import TopBar from "../components/common/TopBar";

// ===============================================================
// Default Settings
// ===============================================================
const DEFAULT_SETTINGS = {
  receiveMode: "both",
  showGifts: true,
  autoGreetingEnabled: true,
  autoGreetingText: "hello",
  notifyNewMessage: true,
  notifyVisit: true,
};

// ===============================================================
// Helper
// ===============================================================
function getUidSafe() {
  return auth.currentUser?.uid || null;
}

function MessagesSettingsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const uid = getUidSafe();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const [gender, setGender] = useState(null);

  // =============================================================
  // Load settings
  // =============================================================
  useEffect(() => {
    const run = async () => {
      try {
        if (!uid) {
          navigate("/");
          return;
        }

        const snap = await getDoc(doc(db, "users", uid));
        if (!snap.exists()) {
          setLoading(false);
          return;
        }

        const data = snap.data() || {};

        setGender(data.gender || null);

        const msg = data.settings?.messages || {};
        setSettings({ ...DEFAULT_SETTINGS, ...msg });

        setLoading(false);
      } catch (e) {
        console.error("Load Messages Settings error:", e);
        setLoading(false);
      }
    };

    run();
  }, [uid, navigate]);

  // =============================================================
  // Block males
  // =============================================================
  useEffect(() => {
    if (gender === "male") {
      navigate("/home");
    }
  }, [gender, navigate]);

  // =============================================================
  // Save settings
  // =============================================================
  const saveSettings = async (nextSettings) => {
    try {
      if (!uid) return;

      setSaving(true);

      await setDoc(
        doc(db, "users", uid),
        {
          settings: {
            messages: {
              ...nextSettings,
              updatedAt: serverTimestamp(),
            },
          },
        },
        { merge: true }
      );

      setSettings(nextSettings);
    } catch (e) {
      console.error("Save Messages Settings error:", e);
    } finally {
      setSaving(false);
    }
  };

  // =============================================================
  // Helpers
  // =============================================================
  const toggle = (key) => saveSettings({ ...settings, [key]: !settings[key] });

  const updateField = (key, value) =>
    saveSettings({ ...settings, [key]: value });

  // =============================================================
  // Labels
  // =============================================================
  const receiveLabel = useMemo(() => {
    if (settings.receiveMode === "messages") return t("receiveMode.messages");
    if (settings.receiveMode === "off") return t("receiveMode.off");
    return t("receiveMode.both");
  }, [settings.receiveMode, t]);

  // =============================================================
  // RENDER
  // =============================================================
  if (loading) {
    return (
      <div className="msgset-page">
        <TopBar title={t("chat_settings")} onBack={() => navigate(-1)} />
        <div className="msgset-loading">{t("loading")}</div>
      </div>
    );
  }

  return (
    <div className="msgset-page">
      <TopBar title={t("chat_settings")} onBack={() => navigate(-1)} />

      <div className="msgset-spacer" />

      <div className="msgset-right">{saving ? "..." : ""}</div>

      {/* Receive Mode */}
      <div className="msgset-card">
        <div className="msgset-card-title">{t("receiveMode.title")}</div>

        <div className="msgset-sub">{t("receiveMode.warning")}</div>

        <div className="msgset-segment">
          <button
            className={
              settings.receiveMode === "both" ? "seg-btn active" : "seg-btn"
            }
            onClick={() => updateField("receiveMode", "both")}
          >
            {t("receiveMode.both")}
          </button>

          <button
            className={
              settings.receiveMode === "messages" ? "seg-btn active" : "seg-btn"
            }
            onClick={() => updateField("receiveMode", "messages")}
          >
            {t("receiveMode.messages")}
          </button>

          <button
            className={
              settings.receiveMode === "off" ? "seg-btn active" : "seg-btn"
            }
            onClick={() => updateField("receiveMode", "off")}
          >
            {t("receiveMode.off")}
          </button>
        </div>

        <div className="msgset-inline">
          <span className="msgset-inline-label">{t("receiveMode.current")}</span>
          <span className="msgset-inline-value">{receiveLabel}</span>
        </div>
      </div>

      {/* Gifts */}
      <div className="msgset-card">
        <div className="msgset-card-title">{t("profilePrivacy.title")}</div>

        <div className="msgset-row">
          <div className="msgset-row-text">
            <div className="msgset-row-title">
              {t("profilePrivacy.showGifts.title")}
            </div>
            <div className="msgset-row-sub">
              {t("profilePrivacy.showGifts.sub")}
            </div>
          </div>

          <label className="switch">
            <input
              type="checkbox"
              checked={!!settings.showGifts}
              onChange={() => toggle("showGifts")}
            />
            <span className="slider" />
          </label>
        </div>
      </div>

      {/* Auto Greeting */}
      <div className="msgset-card">
        <div className="msgset-card-title">{t("autoGreeting.title")}</div>

        <div className="msgset-row">
          <div className="msgset-row-text">
            <div className="msgset-row-title">{t("autoGreeting.enable")}</div>
            <div className="msgset-row-sub">{t("autoGreeting.sub")}</div>
          </div>

          <label className="switch">
            <input
              type="checkbox"
              checked={!!settings.autoGreetingEnabled}
              onChange={() => toggle("autoGreetingEnabled")}
            />
            <span className="slider" />
          </label>
        </div>

        <div className="msgset-field">
          <div className="msgset-field-label">{t("autoGreeting.text")}</div>
          <input
            className="msgset-input"
            value={settings.autoGreetingText || ""}
            onChange={(e) => updateField("autoGreetingText", e.target.value)}
            placeholder={t("autoGreeting.placeholder")}
            disabled={!settings.autoGreetingEnabled}
          />
        </div>
      </div>

      {/* Notifications */}
      <div className="msgset-card">
        <div className="msgset-card-title">{t("notifications.title")}</div>

        <div className="msgset-row">
          <div className="msgset-row-text">
            <div className="msgset-row-title">
              {t("notifications.newMessage.title")}
            </div>
            <div className="msgset-row-sub">
              {t("notifications.newMessage.sub")}
            </div>
          </div>

          <label className="switch">
            <input
              type="checkbox"
              checked={!!settings.notifyNewMessage}
              onChange={() => toggle("notifyNewMessage")}
            />
            <span className="slider" />
          </label>
        </div>

        <div className="msgset-row">
          <div className="msgset-row-text">
            <div className="msgset-row-title">
              {t("notifications.visit.title")}
            </div>
            <div className="msgset-row-sub">{t("notifications.visit.sub")}</div>
          </div>

          <label className="switch">
            <input
              type="checkbox"
              checked={!!settings.notifyVisit}
              onChange={() => toggle("notifyVisit")}
            />
            <span className="slider" />
          </label>
        </div>
      </div>
    </div>
  );
}

export default MessagesSettingsPage;